import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchLatestAssetTrendDataInitialState,
  LatestAssetTrendDataPoint,
} from '../../services/dashboard/LatestAssetTrendDataService';

interface AssetTrendDataState {
  data?: LatestAssetTrendDataPoint[];
  loading: boolean;
  error?: string;
  lastFetchParams?: {
    assetId: string;
    ParamStdTypes?: (string | number)[];
  } | null;
}
const initialState: AssetTrendDataState = {
  data: undefined,
  loading: false,
  error: undefined,
  lastFetchParams: null,
};

// Async thunk for fetching motor power data
export const fetchLatestAssetTrendDataAsync = createAsyncThunk(
  'espLatestAssetTrendData/fetchData',
  async (args: { assetId: string; ParamStdTypes?: (string | number)[] }, { signal }) => {
    const { assetId, ParamStdTypes = [] } = args;
    const response = await fetchLatestAssetTrendDataInitialState(assetId, ParamStdTypes, signal);
    return response?.data as LatestAssetTrendDataPoint[];
  },
  {
    condition: (args, { getState }) => {
      const state = getState() as { EspLatestAssetTrendData: AssetTrendDataState };
      const { lastFetchParams, loading } = state.EspLatestAssetTrendData;

      // If assetId has changed, allow fetch even if loading
      if (lastFetchParams && lastFetchParams.assetId !== args.assetId) {
        return true;
      }

      // Prevent API call if already loading same asset
      if (loading) {
        return false;
      }

      // If no previous params, allow the call
      if (!lastFetchParams) {
        return true;
      }

      // Compare assetId
      if (lastFetchParams.assetId !== args.assetId) {
        return true;
      }

      // Compare ParamStdTypes arrays
      const prevParams = lastFetchParams.ParamStdTypes || [];
      const newParams = args.ParamStdTypes || [];

      if (prevParams.length !== newParams.length) {
        return true;
      }

      const hasParamChanged = prevParams.some((param, index) => param !== newParams[index]);
      return hasParamChanged;
    },
  },
);

const LatestAssetTrendDataSlice = createSlice({
  name: 'EspLatestAssetTrendData',
  initialState,
  reducers: {
    setInitialState: (state) => {
      state.data = undefined;
      state.error = undefined;
      state.loading = false;
      state.lastFetchParams = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLatestAssetTrendDataAsync.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchLatestAssetTrendDataAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.lastFetchParams = action.meta.arg;
      })
      .addCase(fetchLatestAssetTrendDataAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { setInitialState } = LatestAssetTrendDataSlice.actions;
export default LatestAssetTrendDataSlice.reducer;
